import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  HsaConditionExpression,
  HsaConditionLeaf,
  HsaConditionLeafKind,
  HsaMultiSelectConditionOperator,
  HsaTextConditionOperator,
  hsaLeafKindForQuestionType,
} from "./hsa-condition";
import { HsaFlow, HsaFlowNode, HsaFlowNodeType } from "./hsa-flow";
import {
  HSA_QUESTION_POOL,
  HsaQuestionHandle,
  isHsaQuestionHandle,
} from "./hsa-question-pool";
import {
  HsaQuestion,
  HsaQuestionType,
  MULTI_SELECT_NONE_OPTION_VALUE,
  SINGLE_SELECT_OTHER_OPTION_VALUE,
} from "./hsa-question-type";

export enum HsaFlowValidationErrorCode {
  DUPLICATE_NODE_ID = "duplicate_node_id",
  DUPLICATE_GROUP_HANDLE = "duplicate_group_handle",
  DUPLICATE_QUESTION_HANDLE_IN_GROUP = "duplicate_question_handle_in_group",
  DUPLICATE_QUESTION_HANDLE_IN_FLOW = "duplicate_question_handle_in_flow",
  DUPLICATE_CONTENT_HANDLE = "duplicate_content_handle",
  MULTIPLE_CONSENT_NODES = "multiple_consent_nodes",
  QUESTION_HANDLE_NOT_IN_POOL = "question_handle_not_in_pool",
  ROOT_NOT_FOUND = "root_not_found",
  ORPHANED_NODES = "orphaned_nodes",
  ROOT_MUST_BE_GROUP = "root_must_be_group",
  NEXT_ID_NOT_FOUND = "next_id_not_found",
  CYCLE_DETECTED = "cycle_detected",
  UNREACHABLE_NODE = "unreachable_node",
  NO_QUESTIONS_IN_FLOW = "no_questions_in_flow",
  QUESTION_NOT_REACHABLE = "question_not_reachable",
  LEAF_KIND_MISMATCH = "leaf_kind_mismatch",
  OPTION_VALUE_NOT_FOUND = "option_value_not_found",
  VALUE_REQUIRED = "value_required",
  VALUE_UNEXPECTED = "value_unexpected",
  INVALID_REGEX = "invalid_regex",
}

// Errors carry an extra context bundle so callers (especially the MCP layer
// driving an LLM agent) can self-heal: each error tells you not just what
// went wrong but enough specifics to fix it without another lookup. Which
// context fields are populated depends on `code` — consumers should switch
// on `code` to know what to read.
export interface HsaFlowValidationError {
  code: HsaFlowValidationErrorCode;
  nodeId: string;
  message: string;
  // Stable group handle involved in the error (DUPLICATE_GROUP_HANDLE,
  // navigation issues).
  groupHandle?: string;
  // Pool question handle involved in the error (option lookups, leaf-kind
  // mismatches, reachability failures, handle-not-in-pool, duplicates).
  questionHandle?: string;
  // The duplicated value, for DUPLICATE_* errors.
  duplicatedValue?: string;
  // For OPTION_VALUE_NOT_FOUND: the values the condition tried to reference
  // vs. the values that actually exist on the pool question.
  invalidOptionValues?: readonly string[];
  validOptionValues?: readonly string[];
  // For QUESTION_NOT_REACHABLE: question handles that ARE guaranteed to be
  // answered on every path to this condition node — i.e., what the agent
  // could legally reference instead.
  guaranteedReachableQuestions?: readonly string[];
  // For CYCLE_DETECTED: the node ids that form the cycle, in order, with
  // the first id repeated at the end so the loop is visible.
  cyclePath?: readonly string[];
  // For LEAF_KIND_MISMATCH.
  expectedLeafKind?: HsaConditionLeafKind;
  actualLeafKind?: HsaConditionLeafKind;
  expectedQuestionType?: HsaQuestionType;
}

// Wire mirror of HsaFlowValidationError so RPC endpoints can return
// validation errors verbatim for the builder UI to render.
const hsaFlowValidationErrorSchema_ = z.object({
  code: createEnum(HsaFlowValidationErrorCode),
  nodeId: z.string(),
  message: z.string(),
  groupHandle: z.string().optional(),
  questionHandle: z.string().optional(),
  duplicatedValue: z.string().optional(),
  invalidOptionValues: z.array(z.string()).optional(),
  validOptionValues: z.array(z.string()).optional(),
  guaranteedReachableQuestions: z.array(z.string()).optional(),
  cyclePath: z.array(z.string()).optional(),
  expectedLeafKind: createEnum(HsaConditionLeafKind).optional(),
  actualLeafKind: createEnum(HsaConditionLeafKind).optional(),
  expectedQuestionType: createEnum(HsaQuestionType).optional(),
});
export const hsaFlowValidationErrorSchema: z.ZodType<HsaFlowValidationError> =
  hsaFlowValidationErrorSchema_;

// Semantic validation across a flat-array flow. Zod schemas catch shape
// errors; this catches cross-node errors that schemas can't express:
// duplicate ids, dangling id references, cycles, unreachable nodes, handle
// references that don't exist in the pool, condition references to
// questions not guaranteed to be answered upstream, and operator/option
// mismatches.
//
// Returns every error found rather than throwing on the first so the
// builder UI (and the MCP self-healing loop) can surface all issues at
// once.
export function validateHsaFlow(flow: HsaFlow): HsaFlowValidationError[] {
  const errors: HsaFlowValidationError[] = [];
  const nodeById = new Map<string, HsaFlowNode>();

  for (const node of flow.nodes) {
    if (nodeById.has(node.nodeId)) {
      errors.push({
        code: HsaFlowValidationErrorCode.DUPLICATE_NODE_ID,
        nodeId: node.nodeId,
        duplicatedValue: node.nodeId,
        message: `Node id "${node.nodeId}" is used by more than one node. Every node in the flow must have a unique nodeId.`,
      });
      continue;
    }
    nodeById.set(node.nodeId, node);
  }

  validateInternalShape(flow.nodes, errors);
  validateNextIdReferences(flow.nodes, nodeById, errors);

  if (flow.rootId === null) {
    // A null root is only the legitimately-empty flow. Null root with
    // leftover nodes means the trigger edge was disconnected (e.g. an end
    // node inserted on it) and everything downstream silently orphaned.
    const [firstNode] = flow.nodes;
    if (firstNode) {
      errors.push({
        code: HsaFlowValidationErrorCode.ORPHANED_NODES,
        nodeId: firstNode.nodeId,
        message: `The flow start does not connect to any step, but ${flow.nodes.length} ${flow.nodes.length === 1 ? "step remains" : "steps remain"} in the flow. Reconnect the flow start to a question group, or delete the disconnected steps.`,
      });
    }
    return errors;
  }
  if (!nodeById.has(flow.rootId)) {
    errors.push({
      code: HsaFlowValidationErrorCode.ROOT_NOT_FOUND,
      nodeId: flow.rootId,
      message: `rootId "${flow.rootId}" does not match any node in the flow. Set rootId to one of the existing node ids, or add a node with this id.`,
    });
    return errors;
  }
  const rootNode = nodeById.get(flow.rootId);
  if (
    rootNode &&
    rootNode.nodeType !== HsaFlowNodeType.GROUP &&
    rootNode.nodeType !== HsaFlowNodeType.CONTENT
  ) {
    errors.push({
      code: HsaFlowValidationErrorCode.ROOT_MUST_BE_GROUP,
      nodeId: flow.rootId,
      message: `rootId "${flow.rootId}" points to a ${rootNode.nodeType} node. Flows must start on a GROUP or CONTENT node — conditions and knockouts can't run as the first step.`,
    });
    return errors;
  }

  // Cycles short-circuit dominator analysis (it doesn't terminate cleanly
  // on a non-DAG). Flag them first so the merchant fixes those before any
  // reachability errors compound.
  if (detectCycles(flow.rootId, nodeById, errors)) {
    return errors;
  }

  const reachable = computeReachable(flow.rootId, nodeById);
  validateReachability(nodeById, reachable, errors);
  validateHasQuestions(flow.rootId, nodeById, reachable, errors);
  validateConditionReferences(flow.rootId, nodeById, reachable, errors);

  return errors;
}

function validateInternalShape(
  nodes: HsaFlowNode[],
  errors: HsaFlowValidationError[],
): void {
  const seenGroupHandles = new Set<string>();
  const seenFlowQuestionHandles = new Set<string>();
  const seenContentHandles = new Set<string>();
  let consentNodeId: string | null = null;
  for (const node of nodes) {
    switch (node.nodeType) {
      case HsaFlowNodeType.GROUP: {
        if (seenGroupHandles.has(node.groupHandle)) {
          errors.push({
            code: HsaFlowValidationErrorCode.DUPLICATE_GROUP_HANDLE,
            nodeId: node.nodeId,
            groupHandle: node.groupHandle,
            duplicatedValue: node.groupHandle,
            message: `Group handle "${node.groupHandle}" is used by more than one group node. Every group's handle must be unique within the flow.`,
          });
        }
        seenGroupHandles.add(node.groupHandle);

        const seenInGroup = new Set<string>();
        for (const handle of node.questionHandles) {
          if (!isHsaQuestionHandle(handle)) {
            errors.push({
              code: HsaFlowValidationErrorCode.QUESTION_HANDLE_NOT_IN_POOL,
              nodeId: node.nodeId,
              groupHandle: node.groupHandle,
              questionHandle: handle,
              message: `Question handle "${handle}" referenced by group "${node.groupHandle}" does not exist in the question pool. Add the handle to the pool or change the group to reference an existing pool handle.`,
            });
            continue;
          }
          if (seenInGroup.has(handle)) {
            errors.push({
              code: HsaFlowValidationErrorCode.DUPLICATE_QUESTION_HANDLE_IN_GROUP,
              nodeId: node.nodeId,
              groupHandle: node.groupHandle,
              questionHandle: handle,
              duplicatedValue: handle,
              message: `Question handle "${handle}" is listed more than once in group "${node.groupHandle}". A question can only appear once per group.`,
            });
            continue;
          }
          seenInGroup.add(handle);

          if (seenFlowQuestionHandles.has(handle)) {
            errors.push({
              code: HsaFlowValidationErrorCode.DUPLICATE_QUESTION_HANDLE_IN_FLOW,
              nodeId: node.nodeId,
              groupHandle: node.groupHandle,
              questionHandle: handle,
              duplicatedValue: handle,
              message: `Question handle "${handle}" appears in more than one group in this flow. Each pool question can only be referenced by one group so answers map unambiguously to a (group, question) pair.`,
            });
          }
          seenFlowQuestionHandles.add(handle);
        }
        break;
      }
      case HsaFlowNodeType.CONDITION: {
        validateTextValueRules(node.nodeId, node.condition, errors);
        validateMultiSelectValueRules(node.nodeId, node.condition, errors);
        break;
      }
      case HsaFlowNodeType.KNOCKOUT: {
        break;
      }
      case HsaFlowNodeType.CONTENT: {
        if (seenContentHandles.has(node.contentHandle)) {
          errors.push({
            code: HsaFlowValidationErrorCode.DUPLICATE_CONTENT_HANDLE,
            nodeId: node.nodeId,
            duplicatedValue: node.contentHandle,
            message: `Content handle "${node.contentHandle}" is used by more than one content node. Acknowledgements are keyed by content handle, so screens sharing one would share a single answer slot — give every content node a unique handle.`,
          });
        }
        seenContentHandles.add(node.contentHandle);

        if (node.recordsConsent) {
          if (consentNodeId !== null) {
            errors.push({
              code: HsaFlowValidationErrorCode.MULTIPLE_CONSENT_NODES,
              nodeId: node.nodeId,
              duplicatedValue: consentNodeId,
              message: `More than one content node records consent (also node "${consentNodeId}"). Consent proof reads a single consent screen, so mark exactly one content node with recordsConsent.`,
            });
          } else {
            consentNodeId = node.nodeId;
          }
        }
        break;
      }
      default: {
        assertNever(node);
      }
    }
  }
}

function validateTextValueRules(
  nodeId: string,
  expression: HsaConditionExpression,
  errors: HsaFlowValidationError[],
): void {
  for (const block of expression.conditionBlocks) {
    for (const condition of block.conditions) {
      if (condition.leafKind !== HsaConditionLeafKind.TEXT) {
        continue;
      }
      const { operator, value } = condition;
      const isEmptyCheck =
        operator === HsaTextConditionOperator.IS_EMPTY ||
        operator === HsaTextConditionOperator.IS_NOT_EMPTY;
      if (!isEmptyCheck && value === undefined) {
        errors.push({
          code: HsaFlowValidationErrorCode.VALUE_REQUIRED,
          nodeId,
          questionHandle: condition.questionHandle,
          message: `Text condition operator "${operator}" requires a value field. Either set "value" on the condition leaf, or change the operator to is_empty / is_not_empty if no value is intended.`,
        });
      }
      if (isEmptyCheck && value !== undefined) {
        errors.push({
          code: HsaFlowValidationErrorCode.VALUE_UNEXPECTED,
          nodeId,
          questionHandle: condition.questionHandle,
          message: `Text condition operator "${operator}" should not carry a value field. Remove "value" from this condition leaf.`,
        });
      }
      if (
        operator === HsaTextConditionOperator.MATCHES_REGEX &&
        value !== undefined
      ) {
        try {
          new RegExp(value);
        } catch {
          errors.push({
            code: HsaFlowValidationErrorCode.INVALID_REGEX,
            nodeId,
            questionHandle: condition.questionHandle,
            message: `Condition regex "${value}" is not a valid regular expression.`,
          });
        }
      }
    }
  }
}

function validateMultiSelectValueRules(
  nodeId: string,
  expression: HsaConditionExpression,
  errors: HsaFlowValidationError[],
): void {
  for (const block of expression.conditionBlocks) {
    for (const condition of block.conditions) {
      if (condition.leafKind !== HsaConditionLeafKind.MULTI_SELECT) {
        continue;
      }
      const { operator, optionValues } = condition;
      const isEmptyCheck =
        operator === HsaMultiSelectConditionOperator.IS_EMPTY ||
        operator === HsaMultiSelectConditionOperator.IS_NOT_EMPTY;
      if (!isEmptyCheck && optionValues === undefined) {
        errors.push({
          code: HsaFlowValidationErrorCode.VALUE_REQUIRED,
          nodeId,
          questionHandle: condition.questionHandle,
          message: `Multi-select condition operator "${operator}" requires an optionValues field. Either set "optionValues", or change the operator to is_empty / is_not_empty if no values are intended.`,
        });
      }
      if (isEmptyCheck && optionValues !== undefined) {
        errors.push({
          code: HsaFlowValidationErrorCode.VALUE_UNEXPECTED,
          nodeId,
          questionHandle: condition.questionHandle,
          message: `Multi-select condition operator "${operator}" should not carry an optionValues field. Remove "optionValues" from this condition leaf.`,
        });
      }
    }
  }
}

function validateNextIdReferences(
  nodes: HsaFlowNode[],
  nodeById: Map<string, HsaFlowNode>,
  errors: HsaFlowValidationError[],
): void {
  for (const node of nodes) {
    switch (node.nodeType) {
      case HsaFlowNodeType.GROUP: {
        if (node.nextId !== null && !nodeById.has(node.nextId)) {
          errors.push({
            code: HsaFlowValidationErrorCode.NEXT_ID_NOT_FOUND,
            nodeId: node.nodeId,
            groupHandle: node.groupHandle,
            message: `nextId "${node.nextId}" on group "${node.groupHandle}" does not match any node in the flow. Set nextId to an existing node id, or null to terminate the path.`,
          });
        }
        break;
      }
      case HsaFlowNodeType.CONDITION: {
        if (
          node.matchingNextId !== null &&
          !nodeById.has(node.matchingNextId)
        ) {
          errors.push({
            code: HsaFlowValidationErrorCode.NEXT_ID_NOT_FOUND,
            nodeId: node.nodeId,
            message: `matchingNextId "${node.matchingNextId}" on node "${node.nodeId}" does not match any node in the flow. Set matchingNextId to an existing node id, or null to terminate the path.`,
          });
        }
        if (
          node.nonMatchingNextId !== null &&
          !nodeById.has(node.nonMatchingNextId)
        ) {
          errors.push({
            code: HsaFlowValidationErrorCode.NEXT_ID_NOT_FOUND,
            nodeId: node.nodeId,
            message: `nonMatchingNextId "${node.nonMatchingNextId}" on node "${node.nodeId}" does not match any node in the flow. Set nonMatchingNextId to an existing node id, or null to terminate the path.`,
          });
        }
        break;
      }
      case HsaFlowNodeType.KNOCKOUT: {
        break;
      }
      case HsaFlowNodeType.CONTENT: {
        if (node.nextId !== null && !nodeById.has(node.nextId)) {
          errors.push({
            code: HsaFlowValidationErrorCode.NEXT_ID_NOT_FOUND,
            nodeId: node.nodeId,
            message: `nextId "${node.nextId}" on content node "${node.contentHandle}" does not match any node in the flow. Set nextId to an existing node id, or null to terminate the path.`,
          });
        }
        break;
      }
      default: {
        assertNever(node);
      }
    }
  }
}

function outgoingIds(node: HsaFlowGraphNode | undefined): (string | null)[] {
  if (!node) {
    return [];
  }
  switch (node.nodeType) {
    case HsaFlowNodeType.GROUP:
      return [node.nextId];
    case HsaFlowNodeType.CONDITION:
      return [node.matchingNextId, node.nonMatchingNextId];
    case HsaFlowNodeType.KNOCKOUT:
      return [];
    case HsaFlowNodeType.CONTENT:
      return [node.nextId];
    default:
      assertNever(node);
  }
}

// Iterative DFS with white/gray/black coloring. When a back-edge is seen
// (gray target), reconstruct the cycle path from the current DFS stack so
// the agent can see exactly which nodes participate in the loop.
function detectCycles(
  rootId: string,
  nodeById: ReadonlyMap<string, HsaFlowGraphNode>,
  errors: HsaFlowValidationError[],
): boolean {
  const colors = new Map<string, "white" | "gray" | "black">();
  for (const id of nodeById.keys()) {
    colors.set(id, "white");
  }
  const stack: string[] = [];
  let foundCycle = false;
  function visit(id: string): void {
    if (colors.get(id) === "black") {
      return;
    }
    colors.set(id, "gray");
    stack.push(id);
    for (const next of outgoingIds(nodeById.get(id))) {
      if (next === null || !nodeById.has(next)) {
        continue;
      }
      const color = colors.get(next);
      if (color === "gray") {
        const cycleStart = stack.indexOf(next);
        const cyclePath =
          cycleStart >= 0 ? [...stack.slice(cycleStart), next] : [next, next];
        errors.push({
          code: HsaFlowValidationErrorCode.CYCLE_DETECTED,
          nodeId: id,
          cyclePath,
          message: `Edge from "${id}" to "${next}" closes a cycle: ${cyclePath.join(" → ")}. Use set_edge on one of these nodes to break the loop.`,
        });
        foundCycle = true;
        continue;
      }
      if (color === "white") {
        visit(next);
      }
    }
    colors.set(id, "black");
    stack.pop();
  }
  visit(rootId);
  return foundCycle;
}

function computeReachable(
  rootId: string,
  nodeById: ReadonlyMap<string, HsaFlowGraphNode>,
): Set<string> {
  const reachable = new Set<string>();
  const stack: string[] = [rootId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined || reachable.has(id)) {
      continue;
    }
    reachable.add(id);
    for (const next of outgoingIds(nodeById.get(id))) {
      if (next !== null && nodeById.has(next)) {
        stack.push(next);
      }
    }
  }
  return reachable;
}

// Any node in flow.nodes that isn't reachable from rootId is orphaned —
// it can't ever execute. Almost always a structural mistake.
function validateReachability(
  nodeById: Map<string, HsaFlowNode>,
  reachable: Set<string>,
  errors: HsaFlowValidationError[],
): void {
  for (const id of nodeById.keys()) {
    if (reachable.has(id)) {
      continue;
    }
    errors.push({
      code: HsaFlowValidationErrorCode.UNREACHABLE_NODE,
      nodeId: id,
      message: `Node "${id}" is not reachable from rootId. Either remove this node from the flow, or wire an existing reachable node to point at it.`,
    });
  }
}

// A non-empty flow with zero reachable group nodes carrying questions
// can't elicit any answers and would terminate immediately at runtime —
// almost certainly a mistake.
function validateHasQuestions(
  rootId: string,
  nodeById: Map<string, HsaFlowNode>,
  reachable: Set<string>,
  errors: HsaFlowValidationError[],
): void {
  let questionCount = 0;
  for (const id of reachable) {
    const node = nodeById.get(id);
    if (node?.nodeType === HsaFlowNodeType.GROUP) {
      questionCount += node.questionHandles.length;
    }
  }
  if (questionCount === 0) {
    errors.push({
      code: HsaFlowValidationErrorCode.NO_QUESTIONS_IN_FLOW,
      nodeId: rootId,
      message:
        'Flow has zero reachable questions. Customers will hit "done" immediately. Add at least one group with at least one question handle and wire it into the flow before saving.',
    });
  }
}

// For each condition node reachable from root, check that every leaf's
// referenced question is *guaranteed* to have been answered before reaching
// the condition — i.e., that some group containing that questionHandle
// dominates the condition node. Then verify leaf-kind/operator/option-value
// agreement against the pool question.
function validateConditionReferences(
  rootId: string,
  nodeById: Map<string, HsaFlowNode>,
  reachable: Set<string>,
  errors: HsaFlowValidationError[],
): void {
  const dominators = computeDominators(rootId, nodeById, reachable);
  for (const node of nodeById.values()) {
    if (node.nodeType !== HsaFlowNodeType.CONDITION) {
      continue;
    }
    if (!reachable.has(node.nodeId)) {
      continue;
    }
    const dominatorSet = dominators.get(node.nodeId);
    if (!dominatorSet) {
      continue;
    }
    const guaranteedReachableQuestions = collectGuaranteedQuestions(
      dominatorSet,
      nodeById,
      node.nodeId,
    );
    const guaranteedSet = new Set(guaranteedReachableQuestions);
    for (const block of node.condition.conditionBlocks) {
      for (const leaf of block.conditions) {
        validateLeafAgainstFlow(
          node.nodeId,
          leaf,
          guaranteedSet,
          guaranteedReachableQuestions,
          errors,
        );
      }
    }
  }
}

function collectGuaranteedQuestions(
  dominatorSet: Set<string>,
  nodeById: ReadonlyMap<string, HsaFlowGraphNode>,
  selfId: string,
): readonly string[] {
  const handles: string[] = [];
  for (const id of dominatorSet) {
    if (id === selfId) {
      continue;
    }
    const node = nodeById.get(id);
    if (node?.nodeType === HsaFlowNodeType.GROUP) {
      handles.push(...node.questionHandles);
    }
  }
  return handles;
}

// Minimal structural view of a flow accepted by the graph analyses, so the
// editor can run them on partial in-progress flows. HsaFlow is assignable.
export type HsaFlowGraphNode =
  | {
      nodeId: string;
      nodeType: HsaFlowNodeType.GROUP;
      questionHandles: readonly string[];
      nextId: string | null;
    }
  | {
      nodeId: string;
      nodeType: HsaFlowNodeType.CONDITION;
      matchingNextId: string | null;
      nonMatchingNextId: string | null;
    }
  | { nodeId: string; nodeType: HsaFlowNodeType.KNOCKOUT }
  | {
      nodeId: string;
      nodeType: HsaFlowNodeType.CONTENT;
      nextId: string | null;
    };

export interface HsaFlowGraph {
  rootId: string | null;
  nodes: readonly HsaFlowGraphNode[];
}

/**
 * Question handles guaranteed answered on every path from root to `nodeId`.
 * Returns [] when the analysis can't run (no root, cycles, unreachable node).
 */
export function guaranteedQuestionHandles(
  flow: HsaFlowGraph,
  nodeId: string,
): readonly string[] {
  if (flow.rootId === null) {
    return [];
  }
  const nodeById = new Map<string, HsaFlowGraphNode>();
  for (const node of flow.nodes) {
    if (!nodeById.has(node.nodeId)) {
      nodeById.set(node.nodeId, node);
    }
  }
  if (!nodeById.has(flow.rootId)) {
    return [];
  }
  if (detectCycles(flow.rootId, nodeById, [])) {
    return [];
  }
  const reachable = computeReachable(flow.rootId, nodeById);
  if (!reachable.has(nodeId)) {
    return [];
  }
  const dominatorSet = computeDominators(flow.rootId, nodeById, reachable).get(
    nodeId,
  );
  if (!dominatorSet) {
    return [];
  }
  return collectGuaranteedQuestions(dominatorSet, nodeById, nodeId);
}

function validateLeafAgainstFlow(
  conditionNodeId: string,
  leaf: HsaConditionLeaf,
  guaranteedSet: Set<string>,
  guaranteedReachableQuestions: readonly string[],
  errors: HsaFlowValidationError[],
): void {
  // Handle must exist in pool.
  if (!isHsaQuestionHandle(leaf.questionHandle)) {
    errors.push({
      code: HsaFlowValidationErrorCode.QUESTION_HANDLE_NOT_IN_POOL,
      nodeId: conditionNodeId,
      questionHandle: leaf.questionHandle,
      message: `Condition references question handle "${leaf.questionHandle}" which does not exist in the question pool.`,
    });
    return;
  }

  // Handle must be guaranteed answered upstream (dominator analysis).
  if (!guaranteedSet.has(leaf.questionHandle)) {
    const reachableHint =
      guaranteedReachableQuestions.length > 0
        ? `Questions guaranteed answered on every path to this node: ${guaranteedReachableQuestions.join(", ")}.`
        : "No questions are guaranteed answered on every path to this node yet — add a group with the desired question upstream first.";
    errors.push({
      code: HsaFlowValidationErrorCode.QUESTION_NOT_REACHABLE,
      nodeId: conditionNodeId,
      questionHandle: leaf.questionHandle,
      guaranteedReachableQuestions,
      message: `Condition references question "${leaf.questionHandle}", which is not guaranteed to be answered on every path to this node. ${reachableHint} Either change the condition to reference one of those, or restructure the flow so "${leaf.questionHandle}" is in a group on every path leading here.`,
    });
    return;
  }

  // Leaf-kind agreement against the pool question's expected leaf kind.
  const handle = leaf.questionHandle as HsaQuestionHandle;
  const poolQuestion = HSA_QUESTION_POOL[handle];
  const expectedLeafKind = hsaLeafKindForQuestionType(poolQuestion.type);
  if (leaf.leafKind !== expectedLeafKind) {
    errors.push({
      code: HsaFlowValidationErrorCode.LEAF_KIND_MISMATCH,
      nodeId: conditionNodeId,
      questionHandle: leaf.questionHandle,
      expectedLeafKind,
      actualLeafKind: leaf.leafKind,
      expectedQuestionType: poolQuestion.type,
      message: `Condition leaf has leafKind "${leaf.leafKind}" but question "${leaf.questionHandle}" is of type "${poolQuestion.type}" which expects leafKind "${expectedLeafKind}". Either change the leaf kind / operator, or reference a different question.`,
    });
    return;
  }

  // Option-value agreement for select leaves.
  if (
    leaf.leafKind === HsaConditionLeafKind.SINGLE_SELECT ||
    leaf.leafKind === HsaConditionLeafKind.MULTI_SELECT
  ) {
    const validValues = collectOptionValues(poolQuestion.type, handle);
    if (validValues === null) {
      return;
    }
    const validSet = new Set(validValues);
    const invalidValues = (leaf.optionValues ?? []).filter(
      (v) => !validSet.has(v),
    );
    if (invalidValues.length > 0) {
      errors.push({
        code: HsaFlowValidationErrorCode.OPTION_VALUE_NOT_FOUND,
        nodeId: conditionNodeId,
        questionHandle: leaf.questionHandle,
        invalidOptionValues: invalidValues,
        validOptionValues: validValues,
        message: `Condition references option value(s) ${invalidValues.map((v) => `"${v}"`).join(", ")} on question "${leaf.questionHandle}"; valid values are: ${validValues.join(", ")}. Update the condition's optionValues, or add the missing values to the pool question's options.`,
      });
    }
  }
}

// Returns the option value list for select-flavored question types, or
// null if the type doesn't have options. The narrowing is based on the
// pool question's type tag.
function collectOptionValues(
  type: HsaQuestionType,
  handle: HsaQuestionHandle,
): readonly string[] | null {
  const q = HSA_QUESTION_POOL[handle];
  switch (type) {
    case HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION:
    case HsaQuestionType.MULTI_SELECT_QUESTION:
    case HsaQuestionType.MULTI_SELECT_NONE_QUESTION:
      if ("options" in q) {
        return q.options.map((o) => o.value);
      }
      return null;
    case HsaQuestionType.AGE_INPUT_QUESTION:
    case HsaQuestionType.EMAIL_INPUT_QUESTION:
    case HsaQuestionType.TEXT_INPUT_QUESTION:
    case HsaQuestionType.DATE_INPUT_QUESTION:
    case HsaQuestionType.PHONE_INPUT_QUESTION:
    case HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION:
      return null;
    default:
      assertNever(type);
  }
}

// Iterative dataflow dominator algorithm. Returns Map<nodeId, Set<dominators>>
// where dominators[n] = set of nodes through which every path from root to n
// must pass. Only includes nodes reachable from root.
function computeDominators(
  rootId: string,
  nodeById: ReadonlyMap<string, HsaFlowGraphNode>,
  reachable: Set<string>,
): Map<string, Set<string>> {
  const predecessors = new Map<string, Set<string>>();
  for (const id of reachable) {
    predecessors.set(id, new Set());
  }
  for (const id of reachable) {
    for (const next of outgoingIds(nodeById.get(id))) {
      if (next !== null && reachable.has(next)) {
        const set = predecessors.get(next);
        if (set) {
          set.add(id);
        }
      }
    }
  }

  const dom = new Map<string, Set<string>>();
  dom.set(rootId, new Set([rootId]));
  for (const id of reachable) {
    if (id !== rootId) {
      dom.set(id, new Set(reachable));
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const id of reachable) {
      if (id === rootId) {
        continue;
      }
      const predecessorIds = predecessors.get(id);
      let intersection: Set<string> | null = null;
      if (predecessorIds) {
        for (const p of predecessorIds) {
          const predDom = dom.get(p);
          if (!predDom) {
            continue;
          }
          if (intersection === null) {
            intersection = new Set(predDom);
          } else {
            for (const x of intersection) {
              if (!predDom.has(x)) {
                intersection.delete(x);
              }
            }
          }
        }
      }
      const next = new Set(intersection ?? []);
      next.add(id);
      const current = dom.get(id);
      if (!current || !setsEqual(current, next)) {
        dom.set(id, next);
        changed = true;
      }
    }
  }
  return dom;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const x of a) {
    if (!b.has(x)) {
      return false;
    }
  }
  return true;
}

// Pool integrity check. Run as a unit test, not at flow-validation time:
// pool entries are authored TS constants and shouldn't change at runtime,
// so failure here means a code-level mistake. Catches:
//   - SINGLE_SELECT_OTHER_QUESTION must include an option with value "other"
//   - MULTI_SELECT_NONE_QUESTION must include an option with value "none"
//   - duplicate option values within a single question's options
export interface HsaPoolIntegrityError {
  handle: string;
  message: string;
}

export function validateHsaQuestionPool(
  pool: Record<string, HsaQuestion> = HSA_QUESTION_POOL,
): HsaPoolIntegrityError[] {
  const errors: HsaPoolIntegrityError[] = [];
  for (const [handle, question] of Object.entries(pool)) {
    if ("options" in question) {
      const seen = new Set<string>();
      for (const option of question.options) {
        if (seen.has(option.value)) {
          errors.push({
            handle,
            message: `Pool question "${handle}" has a duplicate option value "${option.value}".`,
          });
        }
        seen.add(option.value);
      }
    }
    if (
      question.type === HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION &&
      !question.options.some(
        (o) => o.value === SINGLE_SELECT_OTHER_OPTION_VALUE,
      )
    ) {
      errors.push({
        handle,
        message: `Pool question "${handle}" is SINGLE_SELECT_OTHER_QUESTION but does not include an option with value "${SINGLE_SELECT_OTHER_OPTION_VALUE}".`,
      });
    }
    if (
      question.type === HsaQuestionType.MULTI_SELECT_NONE_QUESTION &&
      !question.options.some((o) => o.value === MULTI_SELECT_NONE_OPTION_VALUE)
    ) {
      errors.push({
        handle,
        message: `Pool question "${handle}" is MULTI_SELECT_NONE_QUESTION but does not include an option with value "${MULTI_SELECT_NONE_OPTION_VALUE}".`,
      });
    }
    // The field is wire-optional for deploy-skew tolerance, so requiredness
    // for authored pool entries is enforced here instead of in the schema.
    if (
      question.type === HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION &&
      question.optionSource === undefined
    ) {
      errors.push({
        handle,
        message: `Pool question "${handle}" is DYNAMIC_MULTI_SELECT_QUESTION but does not declare an optionSource.`,
      });
    }
  }
  return errors;
}
